import { useState, type FormEvent } from 'react';
import type { App } from '@hangar/shared';
import { toast } from 'sonner';
import { useAppMutations } from '@/api/hooks';
import { firstInvalidField, isEmptyBody, shownError } from '@/lib/formModel';
import { useFeature } from '@/lib/permissions';
import { focusElement, useEditFormState } from '@/lib/useFormState';
import { buildEditAppBody, editValuesOf, showsBitriseLink, showsBitriseSetup, validateEditApp, type EditAppField } from './appFormModel';

const STALE_APP_MESSAGE = 'Someone changed this app since you opened it. Close the sheet, reopen it on the reloaded app and save again.';

type EditFormState = ReturnType<typeof useEditFormState>;
type UpdateApp = ReturnType<typeof useAppMutations>['update'];

/** Saves the changed fields; nothing changed closes the sheet without a request. */
function useEditAppSubmit(app: App, onDone: () => void, state: EditFormState, update: UpdateApp, fields: ReturnType<typeof useEditAppFields>) {
  return async (e: FormEvent) => {
    e.preventDefault();
    state.begin();
    const invalid = firstInvalidField(fields.local);
    if (invalid) return focusElement(`edit-app-${invalid}`);
    const body = buildEditAppBody(app, fields.values, { show: fields.showBitrise, touched: fields.bitriseTouched });
    if (isEmptyBody(body)) return onDone();
    try {
      const saved = await update.mutateAsync({ appId: app.id, body, etag: app.etag });
      toast.success(`${saved.name} saved`, { description: `${saved.slug} · ${saved.workingDirectory}` });
      onDone();
    } catch (err) {
      state.fail(err);
    }
  };
}

/** The values of the Edit app form and the messages under each field. */
function useEditAppFields(app: App, state: EditFormState) {
  const [values, setValues] = useState(() => editValuesOf(app));
  const [bitriseTouched, setBitriseTouched] = useState(false);
  const showBitrise = showsBitriseLink(app, useFeature('bitrise') === true);
  const showSetup = showsBitriseSetup(app, useFeature('sourceControl') === true);
  const local = validateEditApp(values);
  return {
    values,
    local,
    bitriseTouched,
    showBitrise,
    showSetup,
    /** The EAS project id also validates while the person types. */
    errorFor: (field: EditAppField) => shownError(field, local, state.server, state.submitted, field === 'easProjectId' && values.easProjectId.length > 0),
    set: (field: EditAppField, value: string) => {
      setValues((v) => ({ ...v, [field]: value }));
      state.clearField(field);
    },
    setBitrise: (slug: string) => {
      setBitriseTouched(true);
      setValues((v) => ({ ...v, bitriseAppSlug: slug }));
    },
  };
}

/** Everything the Edit app form needs: its values, messages, the save handler and the problem alert. */
export function useEditAppForm(app: App, onDone: () => void) {
  const { update } = useAppMutations();
  const state = useEditFormState(STALE_APP_MESSAGE);
  const fields = useEditAppFields(app, state);
  const onSubmit = useEditAppSubmit(app, onDone, state, update, fields);
  return { ...fields, problem: state.problem, bitriseError: state.server.bitriseAppSlug || undefined, pending: update.isPending, onSubmit };
}
